import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  Megaphone, TrendingUp, Settings2, LineChart, Headphones, CalendarClock,
  Compass, PenTool, Boxes, Workflow, Building2,
} from "lucide-react";
import { Reveal } from "../lib/motion";

export const Kicker = ({ children, id }) => (
  <div className="mb-4 flex items-center gap-3" data-testid={id}>
    <span className="h-px w-8 bg-[#b8c0cb]/60" />
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#b8c0cb]">{children}</span>
  </div>
);

const LOGOS = ["NEXA", "Vantage", "HELIX", "Northwind", "QUORUM", "Lumière", "APEX", "Stratus"];

export function TrustedBy() {
  return (
    <section className="border-y border-white/5 bg-[#0b0c0d] py-14" data-testid="trusted-section">
      <p className="mb-9 text-center text-xs uppercase tracking-[0.3em] text-[#8a99a8]">
        Trusted by growing businesses
      </p>
      <div className="marquee-mask">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {LOGOS.concat(LOGOS).map((l, i) => (
            <span
              key={i}
              className="mx-12 font-display text-2xl font-bold tracking-normal text-white/25 transition-colors duration-300 hover:text-white/70"
            >
              {l}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

const EMPLOYEES = [
  { icon: Megaphone, title: "AI Marketing Executive", tags: ["Content", "SEO", "Social media", "Growth"] },
  { icon: TrendingUp, title: "AI Sales Executive", tags: ["Lead generation", "CRM", "Prospecting", "Follow-up"] },
  { icon: Settings2, title: "AI Operations Manager", tags: ["Processes", "Automation", "Project management"] },
  { icon: LineChart, title: "AI Finance Manager", tags: ["Reporting", "Forecasting", "Expenses", "Dashboards"] },
  { icon: Headphones, title: "AI Customer Support", tags: ["Chatbots", "Knowledge base", "Email", "Voice AI"] },
  { icon: CalendarClock, title: "AI Executive Assistant", tags: ["Scheduling", "Meetings", "Docs", "Admin"] },
];

export function WhatWeBuild() {
  return (
    <section id="employees" className="relative mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="employees-section">
      <div className="max-w-2xl">
        <Kicker id="employees-kicker">What we build</Kicker>
        <Reveal>
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-normal md:text-6xl">
            A full workforce, <span className="text-[#8a99a8]">without the payroll.</span>
          </h2>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {EMPLOYEES.map((e, i) => (
          <Reveal key={e.title} delay={i * 0.06}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="lux-card group relative h-full overflow-hidden rounded-2xl p-8"
              data-testid={`employee-card-${i}`}
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/0 blur-3xl transition-all duration-500 group-hover:bg-white/[0.07]" />
              <div className="mb-7 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#b8c0cb]">
                <e.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-bold tracking-normal">{e.title}</h3>
              <div className="mt-5 flex flex-wrap gap-2">
                {e.tags.map((t) => (
                  <span key={t} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-[#8a99a8]">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const STEPS = ["Discover", "Design", "Build", "Automate", "Optimise", "Scale"];

export function GrowthSystem() {
  return (
    <section className="relative overflow-hidden border-y border-white/5 bg-[#0b0c0d] py-28 md:py-36" data-testid="growth-system-section">
      <div className="absolute left-1/2 top-0 h-64 w-[80%] -translate-x-1/2 bg-[#b8c0cb]/5 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-16 max-w-2xl">
          <Kicker id="growth-kicker">Business Growth System</Kicker>
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold tracking-normal md:text-6xl">
              A proven path from idea to impact.
            </h2>
          </Reveal>
        </div>

        <div className="relative">
          <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-white/15 to-transparent md:block" />
          <div className="grid grid-cols-2 gap-y-12 md:grid-cols-6 md:gap-0">
            {STEPS.map((s, i) => (
              <Reveal key={s} delay={i * 0.08} className="relative flex flex-col items-center text-center">
                <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-[#b8c0cb]/30 bg-[#070708] font-mono text-sm text-[#b8c0cb]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="mt-4 font-display text-lg font-bold tracking-normal">{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: Compass, t: "AI Strategy" }, { icon: Workflow, t: "Business Automation" },
  { icon: PenTool, t: "Website Development" }, { icon: Boxes, t: "AI Agent Development" },
  { icon: Settings2, t: "Workflow Automation" }, { icon: TrendingUp, t: "CRM Systems" },
  { icon: Megaphone, t: "SEO" }, { icon: PenTool, t: "Branding" },
  { icon: Headphones, t: "Voice AI" }, { icon: LineChart, t: "Custom AI Solutions" },
];

export function Services() {
  return (
    <section id="services" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36" data-testid="services-section">
      <div className="mb-16 max-w-2xl">
        <Kicker id="services-kicker">Our services</Kicker>
        <Reveal>
          <h2 className="font-display text-4xl font-extrabold tracking-normal md:text-6xl">
            Everything your business needs to run on autopilot.
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {SERVICES.map((s, i) => (
          <Reveal key={s.t} delay={(i % 5) * 0.05}>
            <div
              className="lux-card group flex h-full flex-col justify-between rounded-2xl p-6"
              data-testid={`service-card-${i}`}
            >
              <s.icon className="h-5 w-5 text-[#b8c0cb] transition-transform duration-300 group-hover:-translate-y-1" />
              <p className="mt-10 font-display text-lg font-bold leading-tight tracking-normal">{s.t}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const INDUSTRIES = [
  "Construction", "Professional Services", "Healthcare", "Real Estate", "Finance",
  "Retail", "Hospitality", "Trades", "Manufacturing",
];

export function Industries() {
  return (
    <section className="border-y border-white/5 bg-[#0b0c0d] py-28 md:py-36" data-testid="industries-section">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mb-12 flex items-center gap-3">
          <Building2 className="h-5 w-5 text-[#b8c0cb]" />
          <Kicker id="industries-kicker">Industries</Kicker>
        </div>
        <div className="divide-y divide-white/8 border-y border-white/8">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind} delay={i * 0.03}>
              <div className="group flex items-center justify-between py-6 transition-colors duration-300 hover:bg-white/[0.02]" data-testid={`industry-${i}`}>
                <div className="flex items-baseline gap-6">
                  <span className="font-mono text-xs text-[#8a99a8]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-2xl font-bold tracking-normal text-white/70 transition-colors duration-300 group-hover:text-white md:text-4xl">
                    {ind}
                  </span>
                </div>
                <span className="translate-x-[-8px] text-[#b8c0cb] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                  →
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
